import { CommonModule, DatePipe } from '@angular/common';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { FollowUpsState } from '@app/modules/follow-ups/store/follow-ups.state';
import { LaboratoryState } from '@app/modules/laboratory/store/laboratory.state';
import { PartnerProductConfigurationsState } from '@app/modules/partners/store/partner-product-configurations/partner-product-configurations.state';
import { ReportApprovalSagaState } from '@app/modules/reports/store/report-approval-saga.state';
import { ReportGenerationState } from '@app/modules/reports/store/report-generation.state';
import { ReportsState } from '@app/modules/reports/store/reports.state';
import { ResultsState } from '@app/modules/reports/store/results.state';
import { CustomDateAdapter } from '@core/formatters/custom-date-adapter';
import { CustomDateParserFormatter } from '@core/formatters/custom-date-parser-formatter';
import { accessTokenInterceptor } from '@core/services/access-token-interceptor.service';
import { AppErrorHandler } from '@core/services/app-error.handler';
import { subscriptionKeyInterceptor } from '@core/services/subscription-key-interceptor.service';
import { AccountManagerState } from '@core/store/account-managers/account-managers.state';
import { AnalysisCodesState } from '@core/store/analysis-codes/analysis-codes.state';
import { BundleConfigurationsState } from '@core/store/bundle-configuration/bundle-configuration.state';
import { CountriesState } from '@core/store/countries/countries.state';
import { CouponsState } from '@core/store/coupons/coupons.state';
import { DocumentsState } from '@core/store/documents/documents.state';
import { KitsState } from '@core/store/kits/kits.state';
import { LanguageState } from '@core/store/languages/languages.state';
import { NotificationProfileState } from '@core/store/notification-profiles/notification-profiles.state';
import { PantheonState } from '@core/store/pantheon/pantheon.state';
import { PartnerTypeState } from '@core/store/partner-types/partner-types.state';
import { PartnersState } from '@core/store/partners/partners.state';
import { InvoicesState } from '@core/store/payments/invoice-payments.state';
import { ProductConfigurationsState } from '@core/store/product-configuration/product-configuration.state';
import { ProductsListState } from '@core/store/products/products.state';
import { RunState } from '@core/store/runs/runs.state';
import { DocumentApprovalSagaState } from '@core/store/saga/document-approval-saga.state';
import { SchemeState } from '@core/store/schemes/schemes.state';
import { SigneeState } from '@core/store/signees/signees.state';
import { TemplatesState } from '@core/store/templates/templates.state';
import { UserAccountsState } from '@core/store/user-accounts/user-accounts.state';
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
} from '@ng-bootstrap/ng-bootstrap';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsModule } from '@ngxs/store';
import { environment } from '@root/environments/environment';
import { errorInterceptor } from '@shared/interceptors/error.interceptor';
import { routes } from './app.routes';
import { OrdersState } from './modules/orders/state/orders.state';
import { PatientFormsState } from './modules/patient-forms/state/patient-forms.state';
import { PdfRequestFormModule } from './modules/pdf-request-form/pdf-request-form.module';
import { DiscountState } from './modules/pricing/store/discount.state';
import { PriceRequestState } from './modules/pricing/store/price-request.state';
import { PricingCountryState } from './modules/pricing/store/pricing-country.state';
import { PricingPartnerState } from './modules/pricing/store/pricing-partner.state';
import { ResultImportState } from './modules/reports/store/result-import.state';
import { ProductInventoryItemState } from './modules/service-data/store/product-inventory-item.state';
import { SampleState } from './modules/service-data/store/sample.state';
import { ServiceDataState } from './modules/service-data/store/service-data.state';

const states = [
  AnalysisCodesState,
  LanguageState,
  BundleConfigurationsState,
  CountriesState,
  PartnersState,
  ProductsListState,
  PartnerTypeState,
  ProductConfigurationsState,
  DocumentsState,
  RunState,
  AccountManagerState,
  PantheonState,
  KitsState,
  NotificationProfileState,
  UserAccountsState,
  SigneeState,
  InvoicesState,
  CouponsState,
  ServiceDataState,
  LaboratoryState,
  DocumentApprovalSagaState,
  ReportApprovalSagaState,
  SampleState,
  OrdersState,
  ProductInventoryItemState,
  PatientFormsState,
  PartnerProductConfigurationsState,
  FollowUpsState,
  ResultsState,
  ReportsState,
  ResultImportState,
  ReportGenerationState,
  SchemeState,
  TemplatesState,
  DiscountState,
  PriceRequestState,
  PricingPartnerState,
  PricingCountryState,
];

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    { provide: NgbDateAdapter, useClass: CustomDateAdapter },
    DatePipe,
    importProvidersFrom(
      BrowserModule,
      PdfRequestFormModule,
      CommonModule,
      NgxsModule.forRoot(states, {
        developmentMode: !environment.production,
      })
    ),
    provideRouter(routes, withComponentInputBinding()),
    {
      provide: ErrorHandler,
      useClass: AppErrorHandler,
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        accessTokenInterceptor,
        subscriptionKeyInterceptor,
        errorInterceptor,
      ])
    ),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
        useHttpBackend: true,
      }),
      fallbackLang: 'en',
    }),
    provideAnimations(),
  ],
};
